import React, { useEffect, useState } from "react";
import { SortProvider, STh, useColumnSort } from "@/components/SortTh";
import { Loading, LoadingRow } from "@/components/LoadingState";
import { Plus, Trash2, X, Receipt, Upload, FileDown, MoreVertical, ListOrdered } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { DashboardVisibilityControl } from "@/components/tahsilat/DashboardVisibilityControl";
import { AdvancePlanTable, buildPlan, planSum } from "./advance/AdvancePlanTable";
import { AdvancePlanModal } from "./advance/AdvancePlanModal";
import { BulkPlanModal } from "./advance/BulkPlanModal";
import { AdvanceRequestsPanel } from "./advance/AdvanceRequestsPanel";
import { saveBlob } from "./payroll/bankUtils";

const planSummary = (a) => {
  if (!a.plan?.length) return null;
  const m = a.plan.filter((i) => i.kaynak !== "prim").reduce((s, i) => s + Number(i.tutar), 0);
  const p = a.plan.filter((i) => i.kaynak === "prim").reduce((s, i) => s + Number(i.tutar), 0);
  return `${new Set(a.plan.map((i) => `${i.yil}-${i.ay}`)).size} taksit${m ? ` · maaş ${formatTL(m)}` : ""}${p ? ` · prim ${formatTL(p)}` : ""}`;
};
import { toast } from "sonner";
import { api, AYLAR, formatTL, extractErrorMessage, fmtDate } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DateInput } from "@/components/DateInput";

export default function AdvanceTab({ yil, ay, onChange, refreshKey }) {
  const [advances, setAdvances] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [planFor, setPlanFor] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const toggleSel = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const statement = async (pid) => {
    try {
      const r = await api.get(`/advances/statement/pdf?yil=${yil}&ay=${ay}${pid ? `&personnel_id=${pid}` : ""}`, { responseType: "blob" });
      saveBlob(r, `avans_dokumu_${yil}_${ay}.pdf`);
      toast.success("Avans hesap dökümü indirildi");
    } catch (e) { toast.error(e?.response?.status === 404 ? "Avans kaydı yok" : "Döküm oluşturulamadı"); }
  };
  const { isAdmin, isMuhasebeci, canManageStatements } = useAuth();

  const load = async () => {
    try {
      setLoading(true);
      const [a, p] = await Promise.all([api.get("/advances"), api.get("/personnel")]);
      setAdvances(a.data);
      setPersonnel(p.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const nameOf = (id) => personnel.find((p) => p.id === id)?.ad_soyad || "—";
  const sicilOf = (id) => personnel.find((p) => p.id === id)?.sicil_no || "";
  const advSort = useColumnSort(advances, [], {
    personel: (a) => nameOf(a.personnel_id),
    baslangic: (a) => (a.baslangic_yil || 0) * 100 + (a.baslangic_ay || 0),
    aylik: (a) => (a.taksit_sayisi ? Number(a.toplam_tutar || 0) / a.taksit_sayisi : null),
  });

  const handleDelete = async (id) => {
    if (!window.confirm("Avans kaydı silinsin mi?")) return;
    await api.delete(`/advances/${id}`);
    toast.success("Avans silindi");
    load();
    onChange?.();
  };

  return (
    <>
    {canManageStatements && (
      <div className="flex justify-end">
        <DashboardVisibilityControl kind="avans_tab" title="Avans Yönetimi ekranı" label="Muhasebeci Erişimi" choices={["muhasebeci"]} />
      </div>
    )}
    {(isAdmin || isMuhasebeci) && <AdvanceRequestsPanel isAdmin={isAdmin} onChanged={() => { load(); onChange?.(); }} />}
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 md:p-5 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="font-display text-lg md:text-xl font-bold text-slate-900">Avans Yönetimi</h2>
          <p className="text-xs md:text-sm text-slate-500 mt-0.5">Personel avansları ve taksit planları</p>
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto flex-wrap">
          {isAdmin && sel.size > 0 && (
            <button data-testid="bulk-plan-open" onClick={() => setBulkOpen(true)}
              className="flex items-center gap-2 bg-teal-700 hover:bg-teal-800 text-white px-3 py-2 rounded-lg font-semibold text-sm">
              <ListOrdered size={14} /> Toplu Taksit Planı ({sel.size})
            </button>
          )}
          <button data-testid="advance-statement-all" onClick={() => statement()}
            className="flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 px-3 py-2 rounded-lg font-semibold text-sm">
            <FileDown size={14} /> Hesap Dökümü PDF
          </button>
          <button
            data-testid="advance-import-button"
            onClick={() => setImportOpen(true)}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-lg font-semibold text-sm"
          >
            <Upload size={14} /> <span className="hidden sm:inline">Toplu </span>İçe Aktar
          </button>
          <button
            data-testid="add-advance-button"
            onClick={() => setModalOpen(true)}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-3 md:px-4 py-2 rounded-lg font-semibold text-sm"
          >
            <Plus size={16} /> Avans Ekle
          </button>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-slate-100" data-testid="advance-cards">
        {loading && <Loading />}
        {!loading && advances.length === 0 && <div className="py-10 text-center text-slate-400 text-sm">Henüz avans kaydı yok</div>}
        {advances.map((a) => (
          <div key={a.id} data-testid={`advance-card-${a.id}`} className="p-4 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-slate-900 truncate">{nameOf(a.personnel_id)}</div>
                <div className="font-mono-tab font-bold text-slate-900 text-sm shrink-0">{formatTL(a.toplam_tutar)}</div>
              </div>
              <div className="text-xs text-slate-500 font-mono-tab mt-0.5">TC: {sicilOf(a.personnel_id)} · {fmtDate(a.tarih)}</div>
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div>
                  <span className="text-slate-400">Taksit: </span>
                  <span className="font-mono-tab text-slate-700">{a.taksit_sayisi} ay</span>
                </div>
                <div>
                  <span className="text-slate-400">Başlangıç: </span>
                  <span className="font-mono-tab text-slate-700">{AYLAR[a.baslangic_ay - 1]?.n} {a.baslangic_yil}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-slate-400">Aylık Kesinti: </span>
                  <span className="font-mono-tab font-semibold text-rose-600">{planSummary(a) || formatTL(a.toplam_tutar / a.taksit_sayisi)}</span>
                </div>
                {a.aciklama && (
                  <div className="col-span-2 text-slate-600 italic truncate">{a.aciklama}</div>
                )}
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`advance-actions-${a.id}`}
                  className="shrink-0 w-9 h-9 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
                >
                  <MoreVertical size={18} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onSelect={() => statement(a.personnel_id)} data-testid={`m-statement-advance-${a.id}`}>
                  <FileDown size={14} className="mr-2" /> Hesap Dökümü
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onSelect={() => setPlanFor(a)} data-testid={`m-plan-advance-${a.id}`}>
                    <ListOrdered size={14} className="mr-2" /> Taksit Planı
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => handleDelete(a.id)} className="text-rose-600 focus:text-rose-700" data-testid={`m-del-advance-${a.id}`}>
                  <Trash2 size={14} className="mr-2" /> Sil
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>

      <div className="hidden md:block sticky-table">
        <SortProvider value={advSort}>
        <table className="w-full" data-testid="advance-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {isAdmin && <th className="pl-4 w-8"><input type="checkbox" data-testid="advance-select-all" checked={advances.length > 0 && sel.size === advances.length}
                onChange={() => setSel(sel.size === advances.length ? new Set() : new Set(advances.map((a) => a.id)))} /></th>}
              <Th k="personel">Personel</Th>
              <Th k="tarih">Veriliş Tarihi</Th>
              <Th align="right" k="toplam_tutar">Toplam Avans</Th>
              <Th align="center" k="taksit_sayisi">Taksit</Th>
              <Th align="center" k="baslangic">Başlangıç</Th>
              <Th align="right" k="aylik">Aylık Kesinti</Th>
              <Th k="aciklama">Açıklama</Th>
              <Th align="right"></Th>
            </tr>
          </thead>
          <tbody>
            {loading && <LoadingRow colSpan={9} text="Avanslar yükleniyor..." />}
            {!loading && advances.length === 0 && <tr><td colSpan={9} className="py-10 text-center text-slate-400">Henüz avans kaydı yok</td></tr>}
            {advSort.sorted.map((a) => (
              <tr key={a.id} className={`border-b border-slate-100 hover:bg-slate-50 ${sel.has(a.id) ? "bg-teal-50/50" : ""}`}>
                {isAdmin && <td className="pl-4"><input type="checkbox" checked={sel.has(a.id)} onChange={() => toggleSel(a.id)} data-testid={`advance-select-${a.id}`} /></td>}
                <td className="px-5 py-3 text-sm">
                  <div className="font-semibold text-slate-900">{nameOf(a.personnel_id)}</div>
                  <div className="text-xs text-slate-500 font-mono-tab">TC: {sicilOf(a.personnel_id)}</div>
                </td>
                <td className="px-5 py-3 text-sm font-mono-tab text-slate-700">{fmtDate(a.tarih)}</td>
                <td className="px-5 py-3 text-sm text-right font-mono-tab font-semibold">{formatTL(a.toplam_tutar)}</td>
                <td className="px-5 py-3 text-sm text-center font-mono-tab">{a.taksit_sayisi} ay</td>
                <td className="px-5 py-3 text-sm text-center font-mono-tab">{AYLAR[a.baslangic_ay - 1]?.n} {a.baslangic_yil}</td>
                <td className="px-5 py-3 text-sm text-right font-mono-tab text-rose-600 font-semibold" data-testid={`advance-monthly-${a.id}`}>
                  {planSummary(a) ? <span className="text-xs">{planSummary(a)}</span> : formatTL(a.toplam_tutar / a.taksit_sayisi)}
                </td>
                <td className="px-5 py-3 text-sm text-slate-600">{a.aciklama || "—"}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button data-testid={`statement-advance-${a.id}`} onClick={() => statement(a.personnel_id)} title="Bu personelin avans hesap dökümü (PDF)"
                    className="w-8 h-8 rounded-lg text-slate-600 hover:bg-slate-100 inline-flex items-center justify-center"><FileDown size={14} /></button>
                  {isAdmin && (
                    <button data-testid={`plan-advance-${a.id}`} onClick={() => setPlanFor(a)} title="Taksit planı (aylık tutar, maaştan/primden)"
                      className="w-8 h-8 rounded-lg text-slate-600 hover:bg-slate-100 inline-flex items-center justify-center"><ListOrdered size={14} /></button>
                  )}
                  <button
                    data-testid={`delete-advance-${a.id}`}
                    onClick={() => handleDelete(a.id)}
                    className="w-8 h-8 rounded-lg text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </SortProvider>
      </div>

      {modalOpen && (
        <AdvanceModal
          personnel={personnel}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); onChange?.(); }}
        />
      )}

      {bulkOpen && (
        <BulkPlanModal ids={[...sel]} onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); setSel(new Set()); load(); onChange?.(); }} />
      )}
      {planFor && (
        <AdvancePlanModal advance={planFor} name={nameOf(planFor.personnel_id)} onClose={() => setPlanFor(null)}
          onSaved={() => { setPlanFor(null); load(); onChange?.(); }} />
      )}

      {importOpen && (
        <AdvanceImportModal
          onClose={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); load(); onChange?.(); }}
        />
      )}
    </div>
    </>
  );
}

function AdvanceImportModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    api.get("/advances/import/template", { responseType: "blob" })
      .then((r) => saveBlob(r, "avans_sablon.xlsx")).catch(() => toast.error("Şablon indirilemedi"));
  };

  const handleUpload = async () => {
    if (!file) { toast.error("Önce dosya seçin"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/advances/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      if (res.data.created > 0) {
        toast.success(`${res.data.created} avans kaydı eklendi`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İçe aktarılamadı");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-xl" data-testid="advance-import-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><Upload size={18} /> Toplu Avans İçe Aktar</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
            <p className="font-semibold mb-1">Nasıl Çalışır?</p>
            <ol className="list-decimal list-inside space-y-0.5 text-xs">
              <li>Şablonu indir, her personel için satır ekle</li>
              <li>TC Kimlik No zorunlu — ad soyad bilgi amaçlı</li>
              <li>Veriliş tarihi, toplam tutar, taksit sayısı ve başlangıç ay/yıl gereklidir</li>
              <li>Dosyayı yükle, mevcut avanslara eklenir (üzerine yazmaz)</li>
            </ol>
          </div>
          <button
            data-testid="download-advance-template"
            onClick={downloadTemplate}
            className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-3 rounded-lg font-semibold text-sm transition-colors"
          >
            <FileDown size={16} /> Şablonu İndir (.xlsx)
          </button>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">
              Yüklenecek Dosya
            </label>
            <input
              data-testid="advance-import-file"
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-900 file:text-white file:font-semibold hover:file:bg-slate-800 cursor-pointer"
            />
            {file && (
              <div className="mt-2 text-xs text-slate-500">Seçili: {file.name} ({(file.size / 1024).toFixed(1)} KB)</div>
            )}
          </div>
          {result && (
            <div className={`rounded-lg p-3 text-sm border ${result.created > 0 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
              <div className="font-semibold mb-1">
                {result.created > 0 ? `✓ ${result.created} avans kaydı eklendi` : "Hiç kayıt eklenemedi"}
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-2 text-xs">
                  <div className="font-semibold text-amber-700 mb-1">Uyarılar ({result.errors.length}):</div>
                  <ul className="list-disc list-inside max-h-32 overflow-y-auto space-y-0.5 text-slate-600">
                    {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={result?.created > 0 ? onDone : onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold">
              {result?.created > 0 ? "Kapat & Yenile" : "İptal"}
            </button>
            <button
              data-testid="upload-advance-import"
              onClick={handleUpload}
              disabled={!file || uploading}
              className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50"
            >
              {uploading ? "Yükleniyor..." : "İçe Aktar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ children, align, k }) {
  const cls = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  const full = `px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 ${cls}`;
  if (k) return <STh k={k} className={full}>{children}</STh>;
  return <th className={full}>{children}</th>;
}

function AdvanceModal({ personnel, onClose, onSaved, lockedPersonnelId }) {
  const now = new Date();
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  const [form, setForm] = useState({
    personnel_id: lockedPersonnelId || personnel[0]?.id || "",
    tarih: now.toISOString().split("T")[0],
    toplam_tutar: "",
    taksit_sayisi: 1,
    aylik_kesinti: "",
    baslangic_yil: now.getFullYear(),
    baslangic_ay: now.getMonth() + 1,
    aciklama: "",
  });
  const [saving, setSaving] = useState(false);
  const { isAdmin } = useAuth();
  const [plan, setPlan] = useState(null);
  const regen = (yuvarla = false) => setPlan(buildPlan(form.toplam_tutar, form.taksit_sayisi, form.baslangic_yil, form.baslangic_ay, yuvarla));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.personnel_id || !form.toplam_tutar) {
      toast.error("Personel ve tutar zorunludur");
      return;
    }
    setSaving(true);
    try {
      const { aylik_kesinti, ...rest } = form; // backend doesn't accept this field; it's UI-only
      if (plan && Math.abs(planSum(plan) - Number(form.toplam_tutar)) > 0.01) {
        toast.error("Taksit planı toplamı avans tutarına eşit olmalı");
        setSaving(false);
        return;
      }
      await api.post("/advances", {
        ...rest,
        toplam_tutar: Number(form.toplam_tutar),
        taksit_sayisi: plan ? new Set(plan.map((i) => `${i.yil}-${i.ay}`)).size : Number(form.taksit_sayisi),
        ...(plan ? { baslangic_yil: plan[0].yil, baslangic_ay: plan[0].ay,
          plan: plan.map((i) => ({ yil: i.yil, ay: i.ay, tutar: Number(i.tutar) || 0, kaynak: i.kaynak })) } : {}),
      });
      toast.success("Avans kaydedildi");
      onSaved();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Kaydedilemedi"));
    } finally {
      setSaving(false);
    }
  };

  const monthly = form.toplam_tutar && form.taksit_sayisi ? Number(form.toplam_tutar) / Number(form.taksit_sayisi) : 0;

  const yilOptions = [];
  for (let y = now.getFullYear() + 2; y >= 2022; y--) yilOptions.push(y);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-lg">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><Receipt size={18} /> Yeni Avans</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <Field label="Personel *">
            <select data-testid="advance-personnel-select" required value={form.personnel_id} disabled={!!lockedPersonnelId} onChange={(e) => setForm({ ...form, personnel_id: e.target.value })} className="input disabled:bg-slate-100 disabled:text-slate-700">
              <option value="">Seçin...</option>
              {personnel.map((p) => <option key={p.id} value={p.id}>{p.ad_soyad} ({p.sicil_no})</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Veriliş Tarihi">
              <DateInput value={form.tarih} onChange={(e) => setForm({ ...form, tarih: e.target.value })} className="input" data-testid="advance-tarih" />
            </Field>
            <Field label="Toplam Tutar (₺) *">
              <input
                data-testid="advance-amount"
                required
                type="number"
                step="0.01"
                value={form.toplam_tutar}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => {
                    const next = { ...f, toplam_tutar: v };
                    const vn = Number(v);
                    const tn = Number(f.taksit_sayisi);
                    if (vn > 0 && tn > 0) next.aylik_kesinti = (vn / tn).toFixed(2);
                    return next;
                  });
                }}
                className="input font-mono-tab"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Taksit Sayısı">
              <input
                data-testid="advance-taksit"
                type="number"
                min="1"
                step="1"
                value={form.taksit_sayisi}
                onChange={(e) => {
                  const t = e.target.value;
                  setForm((f) => {
                    const next = { ...f, taksit_sayisi: t };
                    // Keep aylik_kesinti coherent if a toplam is already entered
                    const tn = Number(t);
                    const topl = Number(f.toplam_tutar);
                    if (tn > 0 && topl > 0) {
                      next.aylik_kesinti = (topl / tn).toFixed(2);
                    }
                    return next;
                  });
                }}
                className="input font-mono-tab"
              />
            </Field>
            <Field label="Aylık Kesinti (₺)">
              <input
                disabled={!!plan}
                title={plan ? "Taksit planı aktif · tutarları tablodan düzenleyin" : ""}
                data-testid="advance-aylik-kesinti"
                type="number"
                min="0"
                step="0.01"
                value={form.aylik_kesinti}
                onChange={(e) => {
                  const m = e.target.value;
                  setForm((f) => {
                    const next = { ...f, aylik_kesinti: m };
                    const mn = Number(m);
                    const topl = Number(f.toplam_tutar);
                    if (mn > 0 && topl > 0) {
                      next.taksit_sayisi = Math.ceil(topl / mn);
                    }
                    return next;
                  });
                }}
                placeholder="Opsiyonel · taksit sayısından türetilir"
                className="input font-mono-tab"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Başlangıç Ayı">
              <select value={form.baslangic_ay} onChange={(e) => setForm({ ...form, baslangic_ay: Number(e.target.value) })} className="input">
                {AYLAR.map((m) => <option key={m.v} value={m.v}>{m.n}</option>)}
              </select>
            </Field>
            <Field label="Yıl">
              <select value={form.baslangic_yil} onChange={(e) => setForm({ ...form, baslangic_yil: Number(e.target.value) })} className="input">
                {yilOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </Field>
          </div>
          {isAdmin && (
            <div className="space-y-2" data-testid="advance-plan-section">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Taksit Planı</span>
                <button type="button" data-testid="advance-plan-open" disabled={!Number(form.toplam_tutar)} onClick={() => regen(false)}
                  className="text-[11px] px-2 py-1 border border-slate-300 rounded font-semibold disabled:opacity-40">{plan ? "Yeniden oluştur" : "Ay ay düzenle / primden kes"}</button>
                {plan && <button type="button" data-testid="advance-plan-round" onClick={() => regen(true)} className="text-[11px] px-2 py-1 border border-slate-300 rounded font-semibold">Tam liraya yuvarla</button>}
                {plan && <button type="button" onClick={() => setPlan(null)} className="text-[11px] text-slate-500 hover:underline">Eşit taksite dön</button>}
              </div>
              {plan && <AdvancePlanTable plan={plan} setPlan={setPlan} toplam={form.toplam_tutar} />}
            </div>
          )}
          <Field label="Açıklama">
            <input value={form.aciklama} onChange={(e) => setForm({ ...form, aciklama: e.target.value })} className="input" placeholder="Opsiyonel" />
          </Field>
          {!plan && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
              <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-1">Aylık Kesinti</div>
              <div className="font-mono-tab text-xl font-bold text-emerald-800">{formatTL(monthly)}</div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold">İptal</button>
            <button data-testid="save-advance-button" disabled={saving} className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
      <style>{`.input { width: 100%; padding: 8px 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; background: #fff; } .input:focus { border-color: #0F766E; box-shadow: 0 0 0 3px rgba(15,118,110,0.15); }`}</style>
    </div>
  );
}

export { AdvanceModal };

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
